import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useReactiveVar } from '@apollo/client';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import SelectBankAccount from '../../../selects/SelectBankAccount';
import useSubmitOfferCartOperation from '../../../../hooks/useSubmitOfferCartOperation';
import currencyVar from '../../../../apollo/reactive-variables/money';
import ShoppingCartStepperNavigation from '../../ShoppingCartStepperNavigation';
import ErrorBox from '../../../boxes/ErrorBox';
import ShoppingCartSummaryActionButton from '../../ShoppingCartSummary/ShoppingCartSummaryActionButton';

const VALIDATION_SCHEMA = Yup.object({
  bankAccountId: Yup.string().required('Selecciona una cuenta bancaria para realizar el giro'),
});

const SelectBankAccountStep = ({
  prevStep,
  nextStep,
  certificatePassword,
  isExternal,
  useCustomConfirmOperation,
  CustomConfirmDialog,
  currentStep,
}) => {
  const currency = useReactiveVar(currencyVar);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [onConfirmSubmit, setOnConfirmSubmit] = useState(null);

  const { submitOperation } = useSubmitOfferCartOperation({
    certificatePassword,
    isExternal,
  });

  const executeCreateOperation = useCallback(
    (values, { setSubmitting, setFieldError }) => {
      submitOperation({
        bankAccountId: values.bankAccountId,
        onCompleted: () => {
          setSubmitting(false);
          nextStep();
        },
        onError: (errorMessage) => {
          setFieldError('submit', errorMessage);
          setSubmitting(false);
        },
      });
    },
    [submitOperation, nextStep],
  );

  const handleSubmit = useCallback(
    (values, formikHelpers) => {
      if (useCustomConfirmOperation) {
        // Store the submit function for later execution
        formikHelpers.setSubmitting(false);
        setOnConfirmSubmit(() => () => {
          formikHelpers.setSubmitting(true);
          executeCreateOperation(values, formikHelpers);
        });
        setShowConfirmationDialog(true);
        return;
      }

      executeCreateOperation(values, formikHelpers);
    },
    [executeCreateOperation, useCustomConfirmOperation],
  );

  const handleCancelOnConfirmDialog = () => {
    setShowConfirmationDialog(false);
    setOnConfirmSubmit(null);
  };

  const handleConfirmDialogContinue = () => {
    if (onConfirmSubmit) {
      onConfirmSubmit();
      setShowConfirmationDialog(false);
      setOnConfirmSubmit(null);
    }
  };

  return (
    <>
      <Formik
        initialValues={{ bankAccountId: '' }}
        validationSchema={VALIDATION_SCHEMA}
        onSubmit={handleSubmit}
        validateOnChange
        validateOnBlur
      >
        {({ setFieldValue, errors, isSubmitting, isValid, values }) => (
          <Form style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 16, alignItems: 'center', justifyContent: 'center' }}>
            <Field name="bankAccountId">
              {({ meta }) => (
                <Box style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <SelectBankAccount
                    setBankId={(id) => setFieldValue('bankAccountId', id)}
                    bankId={values.bankAccountId}
                    hideText
                    currency={currency}
                  />
                  {meta.touched && meta.error && (
                    <ErrorBox mt={1}>{meta.error}</ErrorBox>
                  )}
                </Box>
              )}
            </Field>
            {errors.submit && <ErrorBox mt={2}>{errors.submit}</ErrorBox>}
            <ShoppingCartStepperNavigation onBack={prevStep} currentStep={currentStep}>
              <ShoppingCartSummaryActionButton
                id="confirm-bank-account-step-button"
                type="submit"
                label={(
                  <Typography variant="small_3" color="white" fontWeight={600}>
                    Operar con Fingo
                  </Typography>
              )}
                loading={isSubmitting}
                disabled={!isValid || isSubmitting || !values.bankAccountId}
                size="small"
              />
            </ShoppingCartStepperNavigation>
          </Form>
        )}
      </Formik>
      {useCustomConfirmOperation && (
        <CustomConfirmDialog
          open={showConfirmationDialog}
          onClose={handleCancelOnConfirmDialog}
          onConfirmation={handleConfirmDialogContinue}
        />
      )}
    </>
  );
};

SelectBankAccountStep.propTypes = {
  prevStep: PropTypes.func.isRequired,
  nextStep: PropTypes.func.isRequired,
  certificatePassword: PropTypes.string,
  isExternal: PropTypes.bool,
  useCustomConfirmOperation: PropTypes.bool,
  CustomConfirmDialog: PropTypes.elementType,
  currentStep: PropTypes.number,
};

SelectBankAccountStep.defaultProps = {
  certificatePassword: '',
  isExternal: false,
  useCustomConfirmOperation: false,
  CustomConfirmDialog: null,
  currentStep: null,
};

export default SelectBankAccountStep;
